import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { replaceModal } from 'loot-core/src/client/actions';
import { signOut } from 'loot-core/src/client/actions/user';
import { send } from 'loot-core/src/platform/client/fetch';

import { theme } from '../../style';
import { Button } from '../common/Button2';
import { Text } from '../common/Text';
import { View } from '../common/View';

import { type LockReason, isInApp, openStoreSubscriptions } from './billing';

const COPY: Record<LockReason, { title: string; body: string }> = {
  trial: {
    title: 'Your free trial has ended',
    body: 'Your budget and everything you set up are saved. Choose a plan to pick up where you left off.',
  },
  subscription: {
    title: 'Your subscription has ended',
    body: 'Your budget is saved. Subscribe again to get back to it and to your coach.',
  },
  sponsorship: {
    title: 'Your sponsored access has ended',
    body: 'Your budget is saved. Choose a plan to keep going, or ask your coach about another sponsorship.',
  },
  coach: {
    title: 'Your coach access has ended',
    body: "Your MyBudgetCoach coach account is no longer active. If you think this is a mistake, contact us and we'll sort it out.",
  },
};

// Shown instead of the app when a member's trial or subscription has ended.
export function SubscriptionEnded({ reason }: { reason: LockReason }) {
  const dispatch = useDispatch();

  // If they've paid (say, just back from checkout) but their status hasn't
  // caught up, the billing check repairs it on the server; reload so the app
  // picks it up. At most once every two minutes, so it can never loop.
  useEffect(() => {
    const key = 'billing-lock-reloaded-at';
    send('billing-summary', { url: String(window.location.href) }).then(
      result => {
        const summary = result.data as { isCurrent?: boolean } | undefined;
        const last = Number(sessionStorage.getItem(key) || 0);
        if (summary?.isCurrent && Date.now() - last > 2 * 60 * 1000) {
          sessionStorage.setItem(key, String(Date.now()));
          window.location.reload();
        }
      },
    );
  }, []);

  function choosePlan() {
    if (isInApp()) {
      openStoreSubscriptions();
    } else {
      dispatch(replaceModal('subscribe', {}));
    }
  }

  const { title, body } = COPY[reason];

  return (
    <View
      style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        backgroundColor: theme.pageBackground,
      }}
    >
      <View
        role="alert"
        style={{
          maxWidth: 440,
          width: '100%',
          gap: 14,
          padding: 24,
          borderRadius: 8,
          backgroundColor: theme.tableBackground,
          border: `1px solid ${theme.tableBorder}`,
        }}
      >
        <Text style={{ fontSize: 20, fontWeight: 600, color: theme.pageText }}>
          {title}
        </Text>
        <Text style={{ color: theme.pageText, lineHeight: 1.5 }}>{body}</Text>
        <View style={{ flexDirection: 'row', gap: 10, flexWrap: 'wrap' }}>
          {reason !== 'coach' && (
            <Button variant="primary" onPress={choosePlan}>
              Choose a plan
            </Button>
          )}
          <Button
            variant={reason === 'coach' ? 'primary' : 'normal'}
            onPress={() => {
              window.location.href =
                'mailto:admin@mybudgetcoach.com?subject=My%20account';
            }}
          >
            Contact support
          </Button>
        </View>
        <View style={{ alignItems: 'flex-start' }}>
          <Button variant="bare" onPress={() => dispatch(signOut())}>
            <Text style={{ color: theme.pageTextSubdued }}>Log out</Text>
          </Button>
        </View>
      </View>
    </View>
  );
}
